import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspace, MAX_TABS } from '../../store/WorkspaceContext';
import UserDashboard from '../../pages/dashboard/UserDashboard';
import WorkspacePane from './WorkspacePane';
import Breadcrumb from '../navigation/Breadcrumb';

const SPLIT_STORAGE_KEY = 'pj-workspace-split-ratio';
const DEFAULT_SPLIT = 50;
const MIN_SPLIT = 20;
const MAX_SPLIT = 80;
const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const onChange = (event) => setIsMobile(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

function useSplitRatio() {
  const [ratio, setRatio] = useState(() => {
    try {
      const saved = localStorage.getItem(SPLIT_STORAGE_KEY);
      if (saved) {
        const value = parseFloat(saved);
        if (!Number.isNaN(value)) {
          return Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, value));
        }
      }
    } catch {
      // localStorage may be unavailable
    }
    return DEFAULT_SPLIT;
  });

  const persistRatio = useCallback((next) => {
    const clamped = Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, next));
    setRatio(clamped);
    try {
      localStorage.setItem(SPLIT_STORAGE_KEY, String(clamped));
    } catch {
      // ignore persistence failures
    }
  }, []);

  return [ratio, persistRatio];
}

function WorkspacePaneCell({ tab, tabsCount, isActive, onFocus }) {
  const { openTab } = useWorkspace();
  return (
    <div
      onClick={() => onFocus(tab.id)}
      style={{
        minWidth: 0,
        minHeight: 0,
        overflow: 'auto',
        background: 'var(--surface-2)',
        border: `2px solid ${isActive ? '#29AAE1' : 'transparent'}`,
        borderRadius: 12,
        padding: tabsCount === 1 ? 0 : 12,
        transition: 'border-color 0.15s',
        height: '100%',
      }}
    >
      {/* Tabs never change the router URL (see WorkspaceContext.jsx), so the
          global <Breadcrumb> in AppLayout can't reflect what's open in a
          tab - each pane gets its own, fed the tab's own path directly. */}
      <Breadcrumb path={tab.path} onNavigate={(path) => openTab(path, path)} />
      {tab.id === 'home'
        ? <UserDashboard />
        : <WorkspacePane tabId={tab.id} path={tab.path} />}
    </div>
  );
}

function WorkspaceTabBar() {
  const { tabs, activeTabId, closeTab, focusTab } = useWorkspace();
  const tabRefs = useRef({});
  const barRef = useRef(null);

  // Scroll only the tab bar itself, horizontally. scrollIntoView() also
  // scrolls every ancestor (the page/pane), and focusTab() fires on any
  // click inside a pane - so clicking the reply box used to yank the page
  // back up to the tab bar. Keyed on activeTabId only: focusTab also
  // re-creates `tabs` (bringTabToFront) on every click.
  useEffect(() => {
    const bar = barRef.current;
    const el = tabRefs.current[activeTabId];
    if (!bar || !el) return;
    const barRect = bar.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (elRect.left < barRect.left) bar.scrollBy({ left: elRect.left - barRect.left, behavior: 'smooth' });
    else if (elRect.right > barRect.right) bar.scrollBy({ left: elRect.right - barRect.right, behavior: 'smooth' });
  }, [activeTabId]);

  return (
    <div ref={barRef} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '10px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0', overflowX: 'auto' }}>
      {tabs.map((tab) => {
        const active = tab.id === activeTabId;
        return (
          <div
            key={tab.id}
            ref={(el) => { tabRefs.current[tab.id] = el; }}
            onClick={() => focusTab(tab.id)}
            title={tab.title}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              padding: '7px 12px', borderRadius: 9, fontSize: 12.5, fontWeight: active ? 700 : 500,
              whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s',
              background: active ? '#EDF3FC' : '#f8faff',
              color: active ? '#233A7A' : '#64748b',
              border: `1.5px solid ${active ? '#29AAE1' : '#e2e8f0'}`,
            }}
          >
            <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{tab.title}</span>
            {!tab.pinned && (
              <span
                onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
                aria-label="بستن تب"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: 5, color: '#94a3b8' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
              >
                ✕
              </span>
            )}
          </div>
        );
      })}
      <span style={{ marginRight: 'auto', fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
        {tabs.length}/{MAX_TABS} تب باز
      </span>
    </div>
  );
}

function WorkspaceGrid() {
  const { tabs, activeTabId, focusTab } = useWorkspace();
  const isMobile = useIsMobile();
  const [splitRatio, setSplitRatio] = useSplitRatio();

  if (isMobile) {
    return (
      <div className="pj-workspace-mobile" style={{ height: '100%' }}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            style={{
              display: tab.id === activeTabId ? 'block' : 'none',
              height: '100%',
            }}
          >
            <WorkspacePaneCell
              tab={tab}
              tabsCount={1}
              isActive={tab.id === activeTabId}
              onFocus={focusTab}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <DesktopGrid
      tabs={tabs}
      activeTabId={activeTabId}
      focusTab={focusTab}
      ratio={splitRatio}
      setRatio={setSplitRatio}
    />
  );
}

// One stable, keyed layout for every tab count. Previously 1 tab / 2 tabs /
// 3+ tabs each returned a different element tree, so opening or closing a
// ticket remounted every pane - the home list lost its selected filter tab
// (fell back to "unseen") and refetched, and 3+ tabs had no resizable
// splitter at all. Here panes keep their identity (keyed by tab id) and are
// only repositioned with CSS grid placement; the column splitter is
// available from 2 tabs up.
function DesktopGrid({ tabs, activeTabId, focusTab, ratio, setRatio }) {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const count = tabs.length;
  const split = count >= 2;

  const updateRatioFromPointer = useCallback((clientX) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    // RTL layout: first tab sits on the right, so measure from the right edge.
    setRatio(((rect.right - clientX) / rect.width) * 100);
  }, [setRatio]);

  const onPointerDown = useCallback((event) => {
    event.preventDefault();
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.classList.add('pj-workspace-splitter--active');
    document.body.classList.add('pj-workspace-splitter-dragging');
  }, []);

  const onPointerMove = useCallback((event) => {
    if (!draggingRef.current) return;
    updateRatioFromPointer(event.clientX);
  }, [updateRatioFromPointer]);

  const endDrag = useCallback((event) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    event.currentTarget.classList.remove('pj-workspace-splitter--active');
    document.body.classList.remove('pj-workspace-splitter-dragging');
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* already released */ }
  }, []);

  const rows = Math.max(1, Math.ceil(count / 2));
  return (
    <div
      ref={containerRef}
      className="pj-workspace-grid"
      style={{
        display: 'grid',
        height: '100%',
        minHeight: 0,
        gridTemplateColumns: split ? `${ratio}% 12px minmax(0, 1fr)` : '1fr',
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        rowGap: split ? 12 : 0,
      }}
    >
      {tabs.map((tab, i) => (
        <div
          key={tab.id}
          style={{
            minWidth: 0,
            minHeight: 0,
            gridColumn: split ? (i % 2 === 0 ? 1 : 3) : 1,
            gridRow: Math.floor(i / 2) + 1,
          }}
        >
          <WorkspacePaneCell
            tab={tab}
            tabsCount={count}
            isActive={tab.id === activeTabId}
            onFocus={focusTab}
          />
        </div>
      ))}
      {split && (
        <div
          key="splitter"
          className="pj-workspace-splitter"
          role="separator"
          aria-orientation="vertical"
          aria-valuemin={MIN_SPLIT}
          aria-valuemax={MAX_SPLIT}
          aria-valuenow={Math.round(ratio)}
          aria-label="تغییر اندازه پنجره‌ها"
          style={{ gridColumn: 2, gridRow: '1 / -1' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        />
      )}
    </div>
  );
}

// Employee-only multi-tab dashboard (see isWorkspaceRole in roleHelpers.js
// and its usage in App.jsx's /dashboard route, and Sidebar.jsx's menu-click
// interception). Every open tab's page component stays mounted the whole
// time it's open - tabs never unmount/remount on switch, they're just
// visually arranged by gridStyle() and highlighted when active - so there's
// no reload and no duplicated API calls from switching between them.
//
// Note: the actual <WorkspaceProvider> lives in AppLayout.jsx, not here -
// Sidebar.jsx (a sibling of this component, not a descendant) needs to call
// openTab() too when an employee clicks a ticket/mission menu item from
// anywhere else in the app, so the tab state has to be provided above both
// of them, not scoped to just this route's subtree.
export default function MultiTabWorkspace() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <WorkspaceTabBar />
      <div style={{ flex: 1, padding: 12, minHeight: 0 }}>
        <WorkspaceGrid />
      </div>
    </div>
  );
}
